import { memo, useState, useCallback, useEffect } from 'react'
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { vendorAPI } from '../../services/api/vendor'
import { toast } from '../../services/toastService'
import { createLogger } from '../../utils/logger'
import { baseInputClasses, smallLabelClasses, getSelectClasses, getTextareaClasses } from '../../styles/inputStyles'

const logger = createLogger('VendorScheduleManager')

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const EMPTY_SLOT = { dayOfWeek: 1, startTime: '09:00', endTime: '17:00', locationName: '', notes: '' }
const EMPTY_EVENT = { eventDate: '', startTime: '09:00', endTime: '17:00', title: '', locationName: '', notes: '' }

function fmt(timeStr) {
  if (!timeStr) return ''
  const [h, m] = timeStr.split(':').map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`
}

/**
 * VendorScheduleManager - View, add, and delete recurring schedule slots
 * and one-off events for a vendor entity.
 * @idempotent @atomic
 */
const VendorScheduleManager = memo(function VendorScheduleManager({ entityId, t, onSaved }) {
  const [activeTab, setActiveTab] = useState('slots')
  const [slots, setSlots] = useState([])
  const [events, setEvents] = useState([])
  const [loadingList, setLoadingList] = useState(true)

  const fetchSchedule = useCallback(() => {
    if (!entityId) return
    setLoadingList(true)
    vendorAPI.getEntitySchedule(entityId)
      .then(res => {
        setSlots(Array.isArray(res.data?.schedules) ? res.data.schedules : [])
        setEvents(Array.isArray(res.data?.upcoming_events) ? res.data.upcoming_events : [])
      })
      .catch(err => logger.debug('No schedule yet', err))
      .finally(() => setLoadingList(false))
  }, [entityId])

  useEffect(() => { fetchSchedule() }, [fetchSchedule])

  // ── Slot handlers ───────────────────────────────────────────────────────────
  const [newSlot, setNewSlot] = useState(EMPTY_SLOT)
  const [editingSlotId, setEditingSlotId] = useState(null)
  const [editSlot, setEditSlot] = useState(EMPTY_SLOT)
  const { loading: creatingSlot, execute: execCreateSlot } = useAsyncOperation()
  const { execute: execDeleteSlot } = useAsyncOperation()
  const { execute: execUpdateSlot } = useAsyncOperation()

  const startEditSlot = useCallback((slot) => {
    setEditingSlotId(slot.id)
    setEditSlot({ dayOfWeek: slot.dayOfWeek, startTime: slot.startTime, endTime: slot.endTime, locationName: slot.locationName || '', notes: slot.notes || '' })
  }, [])

  const handleUpdateSlot = useCallback(async () => {
    await execUpdateSlot(() => vendorAPI.updateScheduleSlot(entityId, editingSlotId, editSlot), {
      onSuccess: () => { toast.success(t('schedule.slotUpdated', 'Slot updated')); setEditingSlotId(null); fetchSchedule() },
      onError: () => toast.error(t('schedule.saveFailed', 'Failed to save'))
    })
  }, [entityId, editingSlotId, editSlot, execUpdateSlot, t, fetchSchedule])

  const handleCreateSlot = useCallback(async () => {
    if (!newSlot.startTime || !newSlot.endTime) {
      toast.error(t('schedule.timesRequired', 'Start and end times are required'))
      return
    }
    await execCreateSlot(() => vendorAPI.createScheduleSlot(entityId, newSlot), {
      onSuccess: () => {
        toast.success(t('schedule.slotAdded', 'Schedule slot added'))
        setNewSlot(EMPTY_SLOT)
        fetchSchedule()
        onSaved?.()
      },
      onError: (err) => { logger.error('Failed to create slot', err); toast.error(t('schedule.saveFailed', 'Failed to save')) }
    })
  }, [entityId, newSlot, execCreateSlot, t, onSaved, fetchSchedule])

  const handleDeleteSlot = useCallback(async (slotId) => {
    await execDeleteSlot(() => vendorAPI.deleteScheduleSlot(entityId, slotId), {
      onSuccess: () => { toast.success(t('schedule.slotDeleted', 'Slot removed')); fetchSchedule() },
      onError: (err) => { logger.error('Failed to delete slot', err); toast.error(t('schedule.deleteFailed', 'Failed to remove')) }
    })
  }, [entityId, execDeleteSlot, t, fetchSchedule])

  // ── Event handlers ──────────────────────────────────────────────────────────
  const [newEvent, setNewEvent] = useState(EMPTY_EVENT)
  const { loading: creatingEvent, execute: execCreateEvent } = useAsyncOperation()
  const { execute: execDeleteEvent } = useAsyncOperation()

  const handleCreateEvent = useCallback(async () => {
    if (!newEvent.eventDate || !newEvent.title) {
      toast.error(t('schedule.eventFieldsRequired', 'Date and title are required'))
      return
    }
    if (!newEvent.startTime || !newEvent.endTime) {
      toast.error(t('schedule.timesRequired', 'Start and end times are required'))
      return
    }
    await execCreateEvent(() => vendorAPI.createEntityEvent(entityId, newEvent), {
      onSuccess: () => {
        toast.success(t('schedule.eventAdded', 'Event added'))
        setNewEvent(EMPTY_EVENT)
        fetchSchedule()
        onSaved?.()
      },
      onError: (err) => { logger.error('Failed to create event', err); toast.error(t('schedule.saveFailed', 'Failed to save')) }
    })
  }, [entityId, newEvent, execCreateEvent, t, onSaved, fetchSchedule])

  const handleDeleteEvent = useCallback(async (eventId) => {
    await execDeleteEvent(() => vendorAPI.deleteEntityEvent(entityId, eventId), {
      onSuccess: () => { toast.success(t('schedule.eventDeleted', 'Event removed')); fetchSchedule() },
      onError: (err) => { logger.error('Failed to delete event', err); toast.error(t('schedule.deleteFailed', 'Failed to remove')) }
    })
  }, [entityId, execDeleteEvent, t, fetchSchedule])

  return (
    <div className="flex flex-col h-full">
      <div className="flex border-b border-gray-100 flex-shrink-0">
        {['slots', 'events'].map(tab => (
          <button key={tab}
            onClick={() => setActiveTab(tab)}
            onTouchEnd={(e) => { e.preventDefault(); setActiveTab(tab) }}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors outline-none focus:outline-none focus:ring-0 ${
              activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'slots' ? t('schedule.recurringTitle', 'Regular Schedule') : t('schedule.upcomingTitle', 'Events')}
          </button>
        ))}
      </div>

      <div className="overflow-y-auto flex-1 px-4 py-3 space-y-4">
        {loadingList ? (
          <p className="text-xs text-gray-400 text-center py-4">{t('common.loading', 'Loading…')}</p>
        ) : activeTab === 'slots' ? (
          <>
            <SlotList slots={slots} onDelete={handleDeleteSlot} t={t}
              editingSlotId={editingSlotId} editSlot={editSlot}
              onStartEdit={startEditSlot} onEditChange={setEditSlot}
              onSaveEdit={handleUpdateSlot} onCancelEdit={() => setEditingSlotId(null)} />
            <SlotForm slot={newSlot} onChange={setNewSlot} onSubmit={handleCreateSlot} loading={creatingSlot} t={t} />
          </>
        ) : (
          <>
            <EventList events={events} onDelete={handleDeleteEvent} t={t} />
            <EventForm event={newEvent} onChange={setNewEvent} onSubmit={handleCreateEvent} loading={creatingEvent} t={t} />
          </>
        )}
      </div>
    </div>
  )
})

// ── Sub-components (private) ─────────────────────────────────────────────────

function SlotList({ slots, onDelete, onStartEdit, onEditChange, onSaveEdit, onCancelEdit, editingSlotId, editSlot, t }) {
  if (slots.length === 0) return (
    <p className="text-xs text-gray-400 text-center py-2">{t('schedule.noSlots', 'No recurring slots yet')}</p>
  )
  return (
    <ul className="space-y-1.5">
      {slots.map(slot => {
        if (editingSlotId === slot.id) {
          return (
            <li key={slot.id} className="bg-primary/5 border border-primary/20 rounded-lg px-3 py-2 space-y-2">
              <div className="flex gap-2">
                <select value={editSlot.dayOfWeek} onChange={e => onEditChange(p => ({ ...p, dayOfWeek: parseInt(e.target.value) }))}
                  className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 bg-white">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
                <input type="time" value={editSlot.startTime} onChange={e => onEditChange(p => ({ ...p, startTime: e.target.value }))} className="text-xs border border-gray-300 rounded px-2 py-1" />
                <input type="time" value={editSlot.endTime} onChange={e => onEditChange(p => ({ ...p, endTime: e.target.value }))} className="text-xs border border-gray-300 rounded px-2 py-1" />
              </div>
              <input value={editSlot.locationName} onChange={e => onEditChange(p => ({ ...p, locationName: e.target.value }))}
                placeholder={t('schedule.locationPlaceholder', 'Location (optional)')} className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
              <div className="flex gap-2">
                <button type="button" onClick={onSaveEdit} onTouchEnd={(e) => { e.preventDefault(); onSaveEdit() }}
                  className="flex-1 py-1 text-xs bg-primary text-white rounded-lg font-medium hover:bg-primary/90">{t('common.save', 'Save')}</button>
                <button type="button" onClick={onCancelEdit} onTouchEnd={(e) => { e.preventDefault(); onCancelEdit() }}
                  className="flex-1 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">{t('common.cancel', 'Cancel')}</button>
              </div>
            </li>
          )
        }
        return (
          <li key={slot.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2 text-xs">
            <div className="flex-1 min-w-0">
              <span className="font-medium text-gray-700 mr-1.5">
                {t(`schedule.days.${DAY_KEYS[slot.dayOfWeek]}`, DAY_KEYS[slot.dayOfWeek])}
              </span>
              <span className="text-gray-500">{fmt(slot.startTime)} – {fmt(slot.endTime)}</span>
              {slot.locationName && <span className="text-gray-400 ml-1 truncate">@ {slot.locationName}</span>}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button type="button" onClick={() => onStartEdit(slot)} onTouchEnd={(e) => { e.preventDefault(); onStartEdit(slot) }}
                className="text-gray-400 hover:text-primary transition-colors" aria-label="Edit slot">
                <PencilIcon className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => onDelete(slot.id)} onTouchEnd={(e) => { e.preventDefault(); onDelete(slot.id) }}
                className="text-gray-400 hover:text-red-500 transition-colors" aria-label="Remove slot">
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function EventList({ events, onDelete, t }) {
  if (events.length === 0) return (
    <p className="text-xs text-gray-400 text-center py-2">{t('schedule.noEvents', 'No upcoming events yet')}</p>
  )
  return (
    <ul className="space-y-1.5">
      {events.map(ev => {
        const dateStr = new Date(ev.eventDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        return (
          <li key={ev.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2 text-xs">
            <div className="flex-1 min-w-0">
              <span className="font-medium text-gray-700 mr-1.5">{ev.title}</span>
              <span className="text-gray-500">{dateStr} · {fmt(ev.startTime)} – {fmt(ev.endTime)}</span>
              {ev.locationName && <span className="text-gray-400 ml-1 truncate">@ {ev.locationName}</span>}
            </div>
            <button type="button" onClick={() => onDelete(ev.id)} onTouchEnd={(e) => { e.preventDefault(); onDelete(ev.id) }}
              className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" aria-label="Remove event">
              <TrashIcon className="w-4 h-4" />
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function SlotForm({ slot, onChange, onSubmit, loading, t }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">{t('schedule.addSlotHint', 'Add a recurring time slot for a specific day of the week.')}</p>
      <div>
        <label className={smallLabelClasses}>{t('schedule.dayOfWeek', 'Day')}</label>
        <select
          value={slot.dayOfWeek}
          onChange={e => onChange(p => ({ ...p, dayOfWeek: Number(e.target.value) }))}
          className={getSelectClasses()}
        >
          {DAY_KEYS.map((key, idx) => (
            <option key={key} value={idx}>
              {t(`schedule.days.${key}`, key.charAt(0).toUpperCase() + key.slice(1))}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={smallLabelClasses}>{t('schedule.startTime', 'Start')}</label>
          <input
            type="time"
            value={slot.startTime}
            onChange={e => onChange(p => ({ ...p, startTime: e.target.value }))}
            className={baseInputClasses}
          />
        </div>
        <div>
          <label className={smallLabelClasses}>{t('schedule.endTime', 'End')}</label>
          <input
            type="time"
            value={slot.endTime}
            onChange={e => onChange(p => ({ ...p, endTime: e.target.value }))}
            className={baseInputClasses}
          />
        </div>
      </div>
      <div>
        <label className={smallLabelClasses}>{t('schedule.locationName', 'Location (optional)')}</label>
        <input
          type="text"
          value={slot.locationName}
          onChange={e => onChange(p => ({ ...p, locationName: e.target.value }))}
          placeholder={t('schedule.locationPlaceholder', 'e.g. Downtown Park')}
          className={baseInputClasses}
          maxLength={255}
        />
      </div>
      <div>
        <label className={smallLabelClasses}>{t('schedule.notes', 'Notes (optional)')}</label>
        <textarea
          value={slot.notes}
          onChange={e => onChange(p => ({ ...p, notes: e.target.value }))}
          rows={2}
          className={getTextareaClasses()}
        />
      </div>
      <button
        onClick={onSubmit}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        <PlusIcon className="w-4 h-4" />
        {loading ? t('app.loading', 'Saving…') : t('schedule.addSlot', 'Add Slot')}
      </button>
    </div>
  )
}

function EventForm({ event, onChange, onSubmit, loading, t }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">{t('schedule.addEventHint', 'Add a one-time event or special appearance.')}</p>
      <div>
        <label className={smallLabelClasses}>{t('schedule.eventTitle', 'Event Title')}</label>
        <input
          type="text"
          value={event.title}
          onChange={e => onChange(p => ({ ...p, title: e.target.value }))}
          placeholder={t('schedule.eventTitlePlaceholder', 'e.g. Saturday Market')}
          className={baseInputClasses}
          maxLength={255}
        />
      </div>
      <div>
        <label className={smallLabelClasses}>{t('schedule.eventDate', 'Date')}</label>
        <input
          type="date"
          value={event.eventDate}
          min={new Date().toISOString().slice(0, 10)}
          onChange={e => onChange(p => ({ ...p, eventDate: e.target.value }))}
          className={baseInputClasses}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={smallLabelClasses}>{t('schedule.startTime', 'Start')}</label>
          <input
            type="time"
            value={event.startTime}
            onChange={e => onChange(p => ({ ...p, startTime: e.target.value }))}
            className={baseInputClasses}
          />
        </div>
        <div>
          <label className={smallLabelClasses}>{t('schedule.endTime', 'End')}</label>
          <input
            type="time"
            value={event.endTime}
            onChange={e => onChange(p => ({ ...p, endTime: e.target.value }))}
            className={baseInputClasses}
          />
        </div>
      </div>
      <div>
        <label className={smallLabelClasses}>{t('schedule.locationName', 'Location (optional)')}</label>
        <input
          type="text"
          value={event.locationName}
          onChange={e => onChange(p => ({ ...p, locationName: e.target.value }))}
          placeholder={t('schedule.locationPlaceholder', 'e.g. Downtown Park')}
          className={baseInputClasses}
          maxLength={255}
        />
      </div>
      <div>
        <label className={smallLabelClasses}>{t('schedule.notes', 'Notes (optional)')}</label>
        <textarea
          value={event.notes}
          onChange={e => onChange(p => ({ ...p, notes: e.target.value }))}
          rows={2}
          className={getTextareaClasses()}
        />
      </div>
      <button
        onClick={onSubmit}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        <PlusIcon className="w-4 h-4" />
        {loading ? t('app.loading', 'Saving…') : t('schedule.addEvent', 'Add Event')}
      </button>
    </div>
  )
}

export default VendorScheduleManager
